import React, { useState } from 'react';
import { User, UserRole } from '../types';
import { LogOut, Home, FileText, Settings, Users, CreditCard, Menu, X, LogIn, Code2 } from 'lucide-react';

interface LayoutProps {
  user: User;
  onLogout: () => void;
  onNavigate: (view: string) => void;
  onLoginClick?: () => void; // Added for Guest mode
  currentView: string;
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ user, onLogout, onNavigate, onLoginClick, currentView, children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Common Developer Sticker Component (Vertical, Right Side, Fixed)
  const DeveloperSticker = () => (
    <div className="fixed bottom-24 right-0 z-50 pointer-events-none">
      <div className="bg-white/90 backdrop-blur-[2px] border-l border-y border-gray-200 shadow-md rounded-l-lg py-3 px-1.5 flex flex-col items-center gap-2 pointer-events-auto hover:bg-white hover:shadow-lg transition-all duration-300">
         <span className="text-[8px] text-emerald-600 font-bold uppercase tracking-widest opacity-80" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>Developed by</span>
         <div className="w-px h-2 bg-gray-200"></div>
         <span className="text-[9px] font-extrabold text-gray-800 tracking-wider" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>Shabeeb Kayakkandy</span>
      </div>
    </div>
  );

  // GUEST LAYOUT (Clean, No Sidebar, Centered)
  if (user.role === UserRole.GUEST) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col font-sans relative">
        {/* Minimal Guest Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50 px-6 py-4 flex justify-between items-center shadow-sm">
           <div>
              <h1 className="font-bold text-xl text-gray-800 tracking-tight">കായക്കണ്ടി കുടുംബം</h1>
           </div>
           <button 
             onClick={onLoginClick}
             className="text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 px-4 py-2 rounded-md transition-colors flex items-center gap-2"
           >
             <LogIn size={16} />
             <span>Admin Login</span>
           </button>
        </header>

        {/* Main Content - Centered & Clean */}
        <main className="flex-1 w-full max-w-3xl mx-auto p-4 md:p-8 pb-32">
          {children}
        </main>

        <DeveloperSticker />
      </div>
    );
  }

  // ADMIN / REPRESENTATIVE LAYOUT (With Sidebar)
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, roles: [UserRole.MASTER_ADMIN, UserRole.ADMIN] },
    { id: 'survey', label: 'New Survey', icon: FileText, roles: [UserRole.REPRESENTATIVE, UserRole.ADMIN, UserRole.MASTER_ADMIN] },
    { id: 'my-submissions', label: 'My Entries', icon: FileText, roles: [UserRole.REPRESENTATIVE] },
    { id: 'card-gen', label: 'ID Cards', icon: CreditCard, roles: [UserRole.MASTER_ADMIN] },
    { id: 'admin-users', label: 'Manage Admins', icon: Users, roles: [UserRole.MASTER_ADMIN] },
    { id: 'settings', label: 'Survey Settings', icon: Settings, roles: [UserRole.MASTER_ADMIN] },
  ];

  const handleNavigate = (id: string) => {
    onNavigate(id);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row relative font-sans">
      {/* Mobile Header - Admin */}
      <div className="md:hidden bg-emerald-600 text-white p-4 flex justify-between items-center sticky top-0 z-40 shadow-md">
        <h1 className="font-bold text-lg">കായക്കണ്ടി കുടുംബം</h1>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Admin Theme */}
      <aside className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-emerald-700 text-white transform transition-transform duration-300 ease-in-out shadow-2xl flex flex-col
        md:relative md:transform-none md:shadow-none
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="p-6 border-b border-emerald-600 hidden md:block">
          <h1 className="text-xl font-bold tracking-wide">കായക്കണ്ടി കുടുംബം</h1>
          <p className="text-xs text-emerald-200 mt-2 opacity-80 leading-relaxed">അമ്മദ് ഹാജിയുടെ കുടുംബങ്ങളുടെ<br/>വിവര ശേഖരണം</p>
          
          <div className="mt-8 pt-4 border-t border-emerald-600/50">
             <p className="text-sm font-semibold">{user.name}</p>
             <span className="text-[10px] uppercase tracking-wider bg-emerald-800 px-2 py-1 rounded text-emerald-100 mt-1 inline-block">{user.role}</span>
          </div>
        </div>
        
        {/* Mobile Sidebar Header */}
        <div className="p-6 border-b border-emerald-600 md:hidden flex justify-between items-center">
           <div>
             <p className="font-bold">{user.name}</p>
             <span className="text-xs bg-emerald-800 px-2 py-1 rounded text-white">{user.role}</span>
           </div>
           <button onClick={() => setIsMobileMenuOpen(false)}><X size={20} /></button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto flex-1">
          {navItems.filter(item => item.roles.includes(user.role)).map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigate(item.id)}
              className={`w-full flex items-center space-x-3 px-4 py-3 rounded-md transition-all ${
                currentView === item.id ? 'bg-emerald-800 text-white shadow-inner font-medium' : 'hover:bg-emerald-600 text-emerald-100'
              }`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}
          
          <button
            onClick={onLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-md hover:bg-emerald-600 text-emerald-100 transition-colors mt-8 border-t border-emerald-600 pt-6"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 md:p-10 overflow-y-auto h-[calc(100vh-60px)] md:h-screen">
        {children}
      </main>

      <DeveloperSticker />
    </div>
  );
};

export default Layout;